// src/features/editor/components/blocks/previews/InfoBlockPreview.tsx
import { Image as ImageIcon } from 'lucide-react'
import { CanvasBridgeDrop, useCanvasVideoDrop } from './canvasVideoDrop'
import { CanvasImageActions } from './CanvasImageActions'
import { imageUploadBridge } from '@/features/editor/lib/uploadBridge'
import { IMAGE_EXTS } from '@/features/editor/lib/imageFormats'
import { useStrictLang } from './design/strictLangContext'
import { blockTheme, hsl } from '@teach/ui'
import type { InfoBlock } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { PreviewRun } from '../../preview/previewRun'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { httpsMediaSrc } from '@/features/editor/lib/designUrl'
import { BlockLinkButtons } from './BlockLinkButtons'
import { blockBackground } from './blockBackground'
import { PreviewShell } from './PreviewShell'
import { pickMessages, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: InfoBlock
  contentLang: ContentLang
  /** Прохождение: нижняя кнопка ведёт на следующий экран. */
  run?: PreviewRun
}

export const InfoBlockPreview = ({ block, contentLang, run }: Props) => {
  const m = useMessages(messages)
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const canvasDrop = useCanvasVideoDrop()
  // Показ, не правка: у английского курса картинка лежит в imageEn.
  const rawSrc = getLocalizedDisplay(block, 'image', contentLang, strictLang)
  // Авторский URL — гейтить перед DOM: только https, никогда не бросает.
  const src = rawSrc ? httpsMediaSrc(rawSrc) : null

  return (
    <PreviewShell
      cta={p.next}
      onCta={run?.onCta}
      // Как у ученика: плеер кладёт картинку на фон урока и вписывает её целиком,
      // прозрачные края показывают этот фон. Белый — только зона загрузки
      // пустого слайда в редакторе; в прохождении ученик видит фон урока.
      style={{ background: src || run ? blockBackground(block) : hsl(blockTheme.background) }}
      contentClassName={src ? undefined : 'items-center justify-center p-6'}
      // Картинка во весь блок, под прогресс-баром и CTA (слот fullBleed), вписана
      // без обрезки — object-contain, как в плеере.
      fullBleed={
        src ? <img src={src} alt="" className="h-full w-full object-contain" /> : undefined
      }
      aboveCta={<BlockLinkButtons buttons={block.buttons} contentLang={contentLang} />}
    >
      {/* Прохождение: ученик без картинки видит пустой экран с фоном урока, а не
          авторскую подсказку «Загрузите изображение» — превью обещает автору
          экран ученика, и это расхождение было бы ложью. */}
      {src && canvasDrop && !run ? <CanvasImageActions bridge={imageUploadBridge} /> : null}
      {src || run ? null : canvasDrop ? (
        <CanvasBridgeDrop
          bridge={imageUploadBridge}
          accept={{ 'image/*': IMAGE_EXTS }}
          label={m.dropImage}
          subtitle={m.imageFormats}
        />
      ) : (
        // Миниатюра пустого экрана — просто крупная иконка, без подложки и текста.
        <ImageIcon
          data-testid="info-empty-icon"
          className="size-20"
          strokeWidth={1.5}
          style={{ color: hsl(blockTheme.foreground, 0.3) }}
        />
      )}
    </PreviewShell>
  )
}
